import { useEffect, useMemo, useRef } from "react";
import { Smartphone, Monitor, RefreshCw, X, ExternalLink } from "lucide-react";
import { usePreview, labelForTarget } from "../PreviewContext";

export const PreviewPane = () => {
  const { open, setOpen, device, setDevice, target, reloadKey, reload } = usePreview();
  const iframeRef = useRef(null);

  const path = target?.path || "/";
  const anchor = target?.anchor || "";

  // Build iframe src — cache-bust to force reload when reloadKey or path changes.
  const src = useMemo(() => {
    const cb = `_=${reloadKey}`;
    return `${path}?${cb}${anchor}`;
  }, [path, anchor, reloadKey]);

  // After load, scroll iframe to anchor if present (same-origin only).
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !anchor) return;
    const t = setTimeout(() => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const id = anchor.replace(/^#/, "");
        const el = doc.getElementById(id);
        if (el && iframe.contentWindow) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      } catch {
        /* cross-origin or not yet ready */
      }
    }, 350);
    return () => clearTimeout(t);
  }, [anchor, reloadKey, path]);

  if (!open) return null;

  const isMobile = device === "mobile";
  const targetLabel = labelForTarget(target);
  const newTabHref = `${path}${anchor}`;

  return (
    <aside
      className="fixed inset-0 z-40 lg:static lg:inset-auto lg:z-auto bg-[#0a0a0a] lg:bg-[#0c0c0c] border-l border-white/10 flex flex-col"
      data-testid="admin-preview-pane"
    >
      <div className="flex items-center justify-between gap-3 px-4 h-14 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-2 w-2 bg-brand-light rounded-full animate-pulse shrink-0" />
          <div className="leading-tight min-w-0">
            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-light font-bold truncate">
              Live Preview
            </div>
            <div className="text-xs text-zinc-500 truncate" data-testid="preview-target-label">{targetLabel}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <div className="hidden sm:flex items-center bg-black/40 border border-white/10 p-0.5">
            <button
              onClick={() => setDevice("desktop")}
              className={`px-2 py-1 transition-colors ${device === "desktop" ? "bg-brand text-white" : "text-zinc-400 hover:text-white"}`}
              aria-label="Desktop view"
              data-testid="preview-device-desktop"
              title="Desktop view"
            >
              <Monitor className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => setDevice("mobile")}
              className={`px-2 py-1 transition-colors ${device === "mobile" ? "bg-brand text-white" : "text-zinc-400 hover:text-white"}`}
              aria-label="Mobile view"
              data-testid="preview-device-mobile"
              title="Mobile view"
            >
              <Smartphone className="h-3.5 w-3.5" />
            </button>
          </div>
          <button
            onClick={reload}
            className="p-2 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
            aria-label="Refresh preview"
            data-testid="preview-refresh"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <a
            href={newTabHref}
            target="_blank"
            rel="noreferrer"
            className="p-2 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
            aria-label="Open in new tab"
            data-testid="preview-open-newtab"
            title="Open in new tab"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
          <button
            onClick={() => setOpen(false)}
            className="p-2 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors lg:border-l lg:border-white/10 lg:ml-1 lg:pl-3"
            aria-label="Close preview"
            data-testid="preview-close"
            title="Close preview"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto bg-black grid place-items-start">
        <div
          className={`w-full h-full ${isMobile ? "max-w-[390px] mx-auto py-4 px-2" : ""}`}
          style={{ minHeight: "100%" }}
        >
          <iframe
            key={`${path}-${reloadKey}`}
            ref={iframeRef}
            src={src}
            title="BDH live preview"
            className={`w-full h-full bg-black ${isMobile ? "border border-white/15 shadow-2xl rounded-sm" : ""}`}
            style={{ minHeight: isMobile ? "740px" : "100%" }}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </div>
      </div>
    </aside>
  );
};
